import _extends from "@babel/runtime/helpers/extends";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/inherits";

/** @jsx jsx */
import React from 'react';
import Dropzone from 'react-dropzone';
import { css, jsx } from '@emotion/core';
import { FREEDOM_RED_DARK, GRAY_300, OPTIMISTIC_BLUE_LIGHT, WHITE, CHARLES_BLUE, SANS } from '../utilities/constants';

/**
 * Component which allows a user to submit a photo via drag-and-drop, or
 * by choosing a file from their computer.
 *
 * Default appearance can be customized by passing an element into the
 * backgroundElement property; when an image has been selected, its preview
 * will be the same height as that initial element. You may also pass in a
 * static value for the previewHeight.
 *
 * adapted from /services-js/311/components/request/request/QuestionsPane.tsx
 */
var UploadPhoto =
/*#__PURE__*/
function (_React$Component) {
  _inherits(UploadPhoto, _React$Component);

  function UploadPhoto() {
    var _getPrototypeOf2;

    var _this;

    _classCallCheck(this, UploadPhoto);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(UploadPhoto)).call.apply(_getPrototypeOf2, [this].concat(args)));
    _this.dropzoneRef = React.createRef();
    _this.previewRef = React.createRef();
    _this.state = {
      file: _this.props.initialFile || null,
      // jsdom doesn’t have a createObjectURL implementation
      previewUrl: _this.props.initialFile && _this.props.initialFile !== true && typeof URL.createObjectURL !== 'undefined' ? URL.createObjectURL(_this.props.initialFile) : null,
      previewElemPosY: 0
    };

    _this.onDrop = function (files) {
      if (files.length) {
        var _file = files[0];

        _this.setState({
          file: _file,
          previewUrl: URL.createObjectURL(_file)
        }, function () {
          _this.props.handleDrop(_file);
        });
      }
    };

    _this.onRemove = function () {
      if (_this.state.previewUrl) {
        URL.revokeObjectURL(_this.state.previewUrl);
      }

      _this.setState({
        file: null,
        previewUrl: null
      }, function () {
        _this.props.handleRemove();
      });
    };

    _this.handleButtonClick = function () {
      var isUploading = _this.props.uploadProgress && _this.props.uploadProgress < 100;

      if (!_this.dropzoneRef.current) {
        return;
      }

      if (_this.props.errorMessage) {
        _this.onRemove();

        _this.dropzoneRef.current.open();
      } else if (_this.state.file) {
        // User may cancel an upload while it is in progress.
        if (isUploading && _this.props.handleCancel) {
          _this.props.handleCancel();
        }

        _this.onRemove();
      } else {
        _this.dropzoneRef.current.open();
      }

      _this.props.handleButtonClick();
    };

    return _this;
  }

  _createClass(UploadPhoto, [{
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      // see https://react-dropzone.netlify.com/#!/Previews
      // Make sure to revoke the data uris to avoid memory leaks
      if (this.state.previewUrl) {
        URL.revokeObjectURL(this.state.previewUrl);
      }
    }
  }, {
    key: "render",
    value: function render() {
      var _this2 = this;

      var _this$props = this.props,
          errorMessage = _this$props.errorMessage,
          uploadProgress = _this$props.uploadProgress,
          acceptTypes = _this$props.acceptTypes;
      var file = this.state.file;
      var previewUrl = file ? this.state.previewUrl || this.props.defaultPreviewUrl : null;
      var isUploading = uploadProgress && uploadProgress < 100;
      var uploadProgNum = typeof uploadProgress === 'number' ? uploadProgress : 0;
      var uploadProgressLabel = Math.round(uploadProgNum);
      var hasError = !!errorMessage;
      var buttonTitle;

      if (isUploading) {
        buttonTitle = this.props.buttonTitleCancel || 'Cancel upload';
      } else if (errorMessage) {
        buttonTitle = errorMessage;
      } else if (file) {
        buttonTitle = this.props.buttonTitleRemove || 'Remove photo';
      } else {
        buttonTitle = this.props.buttonTitleUpload || 'Upload photo';
      }

      if (uploadProgressLabel === 100 && errorMessage && errorMessage.length > 0) {
        buttonTitle = errorMessage;
      }

      if (errorMessage && errorMessage.length > 0 && uploadProgressLabel < 100) {
        buttonTitle = errorMessage;
      }

      return jsx("div", {
        className: "br br-a200",
        css: ELEM_WRAPPER
      }, jsx(Dropzone, {
        ref: this.dropzoneRef,
        accept: acceptTypes,
        multiple: false,
        onDrop: this.onDrop
      }, function (_ref) {
        var getRootProps = _ref.getRootProps,
            getInputProps = _ref.getInputProps,
            isDragActive = _ref.isDragActive;
        return jsx("div", _extends({}, getRootProps(), {
          css: INPUT_CONTAINER_FOCUS_STYLING
        }), jsx("input", getInputProps()), jsx("div", {
          ref: _this2.previewRef,
          css: [PREVIEW_CONTAINER_STYLING, isDragActive && DRAG_RING_STYLING]
        }, jsx("div", {
          style: {
            visibility: previewUrl ? 'hidden' : 'visible'
          }
        }, _this2.props.backgroundElement || defaultInitialAppearance()), previewUrl && jsx("div", {
          css: PREVIEW_IMAGE_STYLING,
          style: {
            backgroundImage: "url(".concat(previewUrl, ")")
          }
        })));
      }), !!isUploading && !errorMessage && jsx("div", {
        css: PROGRESS_WRAPPER_STYLING,
        style: {
          top: "".concat(this.state.previewElemPosY)
        }
      }, jsx("label", {
        css: PROGRESS_LABEL_STYLING
      }, "Uploading: ", uploadProgressLabel, "%"), jsx("div", {
        css: PROGRESS_STYLING,
        style: {
          width: "".concat(uploadProgress, "%")
        }
      }, uploadProgress)), jsx("div", {
        className: "br br-t200",
        style: {
          position: 'relative'
        }
      }, jsx("button", {
        className: "btn btn--w btn--b ".concat(isUploading ? 'btn--r-hov' : ''),
        css: [BUTTON_FOCUS_STYLING, hasError && ERROR_MESSAGE_STYLING],
        style: {
          width: '100%'
        },
        type: "button",
        onClick: this.handleButtonClick
      }, buttonTitle)));
    }
  }]);

  return UploadPhoto;
}(React.Component);
/**
 * Defaults to a camera icon.
 */


UploadPhoto.defaultProps = {
  buttonTitleUpload: 'Upload photo',
  buttonTitleRemove: 'Remove photo',
  buttonTitleCancel: 'Cancel upload',
  acceptTypes: 'image/*',
  defaultPreviewUrl: 'https://patterns.boston.gov/images/global/icons/experiential/pdf-doc.svg'
};
export { UploadPhoto as default };

function defaultInitialAppearance() {
  return jsx("div", {
    css: DEFAULT_IMAGE_STYLING
  }, jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 22 144 100",
    "aria-hidden": "true",
    focusable: "false"
  }, jsx("polygon", {
    points: "93.58 51.5 87.75 41.5 59.55 41.5 54.35 51.5 27.18 51.5 27.18 102.5 116.82 102.5 116.82 51.5 93.58 51.5"
  }), jsx("rect", {
    x: "31.48",
    y: "44.49",
    width: "15.23",
    height: "7.01"
  }), jsx("circle", {
    cx: "74",
    cy: "74.38",
    r: "22.88"
  }), jsx("circle", {
    cx: "74",
    cy: "74.38",
    r: "17.47"
  }), jsx("circle", {
    cx: "38.63",
    cy: "60.15",
    r: "3.24"
  }), jsx("line", {
    x1: "96.12",
    y1: "67.86",
    x2: "116.82",
    y2: "67.86"
  }), jsx("line", {
    x1: "27.18",
    y1: "67.86",
    x2: "51.88",
    y2: "67.86"
  })));
}

var ELEM_WRAPPER = css({
  position: 'relative',
  overflow: 'hidden'
});
var PROGRESS_WRAPPER_STYLING = css({
  display: 'block',
  position: 'absolute',
  left: 0,
  lineHeight: '1.5em',
  minHeight: 1,
  borderBottomWidth: 2,
  borderBottomColor: CHARLES_BLUE,
  borderBottomStyle: 'solid',
  width: '100%',
  backgroundColor: GRAY_300,
  textIndent: '-9000'
});
var PROGRESS_STYLING = css({
  color: OPTIMISTIC_BLUE_LIGHT,
  lineHeight: '1.5em',
  minHeight: 1,
  height: 'auto',
  backgroundColor: OPTIMISTIC_BLUE_LIGHT,
  borderRightWidth: 1,
  borderRightColor: CHARLES_BLUE,
  borderRightStyle: 'solid',
  textIndent: '-9000px'
});
var PROGRESS_LABEL_STYLING = css({
  fontSize: '1em',
  fontFamily: SANS,
  color: WHITE,
  textAlign: 'center',
  position: 'absolute',
  top: 0,
  width: '100%',
  height: '100%'
});
var DEFAULT_IMAGE_STYLING = css({
  backgroundColor: GRAY_300,
  color: WHITE,
  svg: {
    strokeWidth: 3,
    strokeLinecap: 'round',
    fill: 'none',
    stroke: 'currentColor'
  }
});
var INPUT_CONTAINER_FOCUS_STYLING = css({
  '&:focus > div': {
    outline: "4px solid #000",
    outlineOffset: '-5px'
  }
});
var BUTTON_FOCUS_STYLING = css({
  '&:focus': {
    outline: "3px solid #000",
    outlineOffset: '-4px'
  }
});
var PREVIEW_CONTAINER_STYLING = css({
  position: 'relative',
  cursor: 'pointer',
  overflow: 'hidden',
  backgroundColor: 'currentColor'
});
var PREVIEW_IMAGE_STYLING = css({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center center'
});
var DRAG_RING_STYLING = css({
  '&::before': {
    display: 'block',
    position: 'absolute',
    content: '""',
    top: '0',
    left: '0',
    bottom: '0',
    right: '0',
    borderStyle: 'dashed',
    borderColor: WHITE
  }
});
var ERROR_MESSAGE_STYLING = css({
  color: FREEDOM_RED_DARK
});